import { Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { FileUploadModule } from 'ng2-file-upload';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Observable, of } from 'rxjs';
import { ColumnDefinition } from 'src/app/controls/advanced-gridview/models/column-definition';
import { ColumnListDefinition } from 'src/app/controls/advanced-gridview/models/column-list-definition';
import { ColumnType } from 'src/app/controls/advanced-gridview/models/column-type';
import { Context } from 'src/app/controls/advanced-gridview/models/context';
import { SortInfos } from 'src/app/controls/advanced-gridview/models/sort-infos';
import { AdvancedService } from 'src/app/controls/advanced-gridview/services/advanced-service';
import { CrudFormComponent } from 'src/app/controls/crud-form/crud-form.component';
import { FilterType } from 'src/app/controls/filters/models/filter-type';
import { ListItem } from 'src/app/controls/filters/models/list-item';
import { EnumHelper } from 'src/app/helpers/enum-helper';
import { Rights } from 'src/app/models/enums/rights';
import { IListLayout } from 'src/app/models/interfaces/list-layout.interface';
import { ISortable } from 'src/app/models/interfaces/sortable.interface';
import { ListLayoutColumn } from 'src/app/models/list-layout-column';
import { AttributeDefinitionsService } from 'src/app/services/attribute-definitions.service';
import { ListLayoutColumnsService } from 'src/app/services/list-layout-columns.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { AdvancedGridviewComponent } from '../../../controls/advanced-gridview/advanced-gridview.component';
import { ListLayoutColumnFormDialogComponent } from '../../list-layouts-workflows/list-layout-workflows-columns-form/list-layout-column-form-dialog/list-layout-column-form-dialog.component';

@Component({
    selector: 'app-list-layout-boxes-columns-form',
    templateUrl: '../../../controls/crud-form/crud-form.component.html',
    styleUrls: ['../../../controls/crud-form/crud-form.component.scss'],
    imports: [AdvancedGridviewComponent, MatButtonModule, FileUploadModule, NgxSpinnerModule],
})
export class ListLayoutBoxesColumnsFormComponent extends CrudFormComponent<ListLayoutColumn, IListLayout, ListLayoutColumnFormDialogComponent> {
    private _sizeInPercent: boolean;

    @Input()
    public set sizeInPercent(value: boolean) {
        this._sizeInPercent = value;
    }

    public get sizeInPercent(): boolean {
        return this._sizeInPercent;
    }

    /**
     * Constructeur.
     * @param enumHelper: Classe utilitaire pour les énumérations.
     * @param notificationService: Service utilisé pour gérer l'affichage des notifications.
     * @param spinner: Service utilisé pour gérer l'affichage du spinner.
     * @param dialog: Service pour ouvrir des boites de dialogue.
     * @param memoryService: Service pour récupérer les informations en mémoire.
     */
    public constructor(
        private listLayoutColumnsService: ListLayoutColumnsService,
        private attributeDefinitionsService: AttributeDefinitionsService,
        public override enumHelper: EnumHelper,
        protected override notificationService: NotificationService,
        protected override spinner: NgxSpinnerService,
        public override dialog: MatDialog,
        public override memoryService: MemoryService
    ) {
        super(enumHelper, notificationService, spinner, dialog, memoryService);
    }

    /**
     * Définition des colonnes de la Gridview.
     */
    override get defaultTableConfig(): ColumnDefinition[] {
        return [
            new ColumnListDefinition<ListLayoutColumn>('attributeId', 'Attribut', FilterType.List, 1, 70, true, false),
            new ColumnDefinition(
                'isStickyColumn',
                'Colonne figée',
                ColumnType.Text,
                FilterType.Text,
                2,
                10,
                true,
                "Une colonne figée est une colonne qui reste figée lorsque l'on fait défiler horizontalement la liste"
            ),
            new ColumnDefinition('size', this.sizeInPercent === true ? 'Taille en %' : 'Taille en px', ColumnType.Text, FilterType.Text, 3, 5, true),
            new ColumnDefinition('position', 'Position', ColumnType.Text, FilterType.Text, 4, 10, true),
        ];
    }

    /**
     * Définition des colonnes de la Gridview.
     */
    override get withIdTableConfig(): ColumnDefinition[] {
        return [
            new ColumnDefinition('id', 'Id', ColumnType.Text, FilterType.Text, 1, 5, true),
            new ColumnListDefinition<ListLayoutColumn>('attributeId', 'Attribut', FilterType.List, 2, 65, true, false),
            new ColumnDefinition(
                'isStickyColumn',
                'Colonne figée',
                ColumnType.Text,
                FilterType.Text,
                3,
                10,
                true,
                "Une colonne figée est une colonne qui reste figée lorsque l'on fait défiler horizontalement la liste"
            ),
            new ColumnDefinition('size', this.sizeInPercent === true ? 'Taille en %' : 'Taille en px', ColumnType.Text, FilterType.Text, 4, 5, true),
            new ColumnDefinition('position', 'Position', ColumnType.Text, FilterType.Text, 5, 10, true),
        ];
    }

    /**
     * Charge les éléments des filtres d'une colonnes de type liste.
     * @param datasourceContext
     * @param columns
     */
    protected override loadListColumnDatas(datasourceContext: Context, columns: ColumnListDefinition<string>[]): void {
        this.attributeDefinitionsService.List(datasourceContext, null, null, null).subscribe((x) => {
            var listItem = x.value.map<ListItem<string>>(
                (v) =>
                    <ListItem<string>>{
                        value: v.id.toString(),
                        text: v.name,
                    }
            );
            listItem.push({ value: '-1', text: 'Icônes système' });
            listItem.push({ value: '-2', text: 'Colonne action' });
            this.setColumnFilterDatas(
                columns.find((x) => x.columnName === 'attributeId'),
                listItem
            );
        });
    }

    /**
     * Instancie un nouvel enregistrement.
     */
    protected override instantiateNewRecord(): ListLayoutColumn {
        let record = new ListLayoutColumn();
        return record;
    }

    /**
     * Retourne le texte de confirmation de la suppression.
     */
    protected override getDeleteConfirmationText(item: ListLayoutColumn): string {
        return `Vous êtes sur le point de supprimer la colonne '${item.id}'.`;
    }

    /**
     * Retourne le texte de suppression.
     */
    protected override getDeletionText(): string {
        return 'Colonne supprimée';
    }

    /**
     * Retourne le texte d'erreur de suppression.
     */
    protected override getDeletionErrorText(): string {
        return 'Impossible de supprimer la colonne.';
    }

    /**
     * Retourne le texte de création.
     */
    protected override getCreationText(): string {
        return 'Colonne créée';
    }

    /**
     * Retourne le texte d'erreur de création.
     */
    protected override getCreationErrorText(): string {
        return 'Impossible de créer la colonne.';
    }

    /**
     * Retourne le texte de mise à jour.
     */
    protected override getUpdateText(): string {
        return 'Colonne mise à jour';
    }

    /**
     * Retourne le texte d'erreur de mise à jour.
     */
    protected override getUpdateErrorText(): string {
        return 'Impossible de mettre à jour la colonne.';
    }

    /**
     * Retourne le texte de changement d'ordre.
     * A surcharger.
     */
    protected override getApplyOrderChangesText(): string {
        return 'Ordre des colonnes mis à jour';
    }

    /**
     * Retourne le texte d'erreur de changement d'ordre.
     * A surcharger.
     */
    protected override getApplyOrderChangesErrorText(): string {
        return "Impossible de mettre à jour l'ordre des colonnes.";
    }

    /**
     * Retourne la requête pour supprimer un enregistrement.
     * @param item : Instance de l'enregistrement à supprimer.
     */
    protected override getDeletionRequest(item: ListLayoutColumn): Observable<boolean> {
        return this.listLayoutColumnsService.delete(this._context.item.id, item.id);
    }

    /**
     * Retourne la requête pour créer un enregistrement.
     * @param item : Instance de l'enregistrement à créer.
     */
    protected override getCreationRequest(item: ListLayoutColumn): Observable<ListLayoutColumn> {
        return this.listLayoutColumnsService.add(this._context.item.id, item);
    }

    /**
     * Retourne la requête pour mettre à jour un enregistrement.
     * @param item : Instance de l'enregistrement à mettre à jour.
     */
    protected override getUpdateRequest(item: ListLayoutColumn): Observable<ListLayoutColumn> {
        return this.listLayoutColumnsService.update(this._context.item.id, item.id, item);
    }

    /**
     * Retourne la requête pour uploader le fichier d'import.
     */
    protected override getUploadRequest(): string {
        return `${this.baseUrl}/api/organizations/${this.organizationId}/views/${this.viewId}/listlayouts/${this._context.item.id}/columns/import`;
    }

    /**
     * Retourne la requête pour mettre à jour l'ordre des enregistrements.
     * A surcharger.
     * @param items : Liste des instances d'enregistrements dont l'ordre doit être modifié.
     */
    protected override getApplyOrderChangesRequest(items: ISortable[]): Observable<boolean> {
        const sortedItems: ListLayoutColumn[] = [];

        items.forEach((x) => {
            const sortedItem = this.dataSource.datas.find((y) => y.attributeId === x.id);
            sortedItem.position = x.order;
            sortedItems.push(sortedItem);
        });

        return this.listLayoutColumnsService.applyOrderChanges(this._context.item.id, sortedItems);
    }

    /**
     * Retourne le composant boite de dialogue utilisé pour afficher le formulaire de création / édition / consultation.
     */
    protected override getDialogComponent(): new (...args: any[]) => ListLayoutColumnFormDialogComponent {
        return ListLayoutColumnFormDialogComponent;
    }

    /**
     * Retourne le nom du fichier d'export.
     */
    protected override getExportFileName(): string {
        return `Colonnes de la mise en page ${this._context.item.id}.xlsx`;
    }

    /**
     * Retourne le duplicata d'un enregistrement.
     * @param item : Instance de l'enregistrement à dupliquer.
     */
    protected override duplicate(item: ListLayoutColumn): ListLayoutColumn {
        let record = new ListLayoutColumn();
        record.attributeId = item.attributeId;
        record.isStickyColumn = item.isStickyColumn;
        record.size = item.size;
        return record;
    }

    /**
     * Retourne la source de données de la liste.
     */
    protected override getDatasourceService(): AdvancedService<ListLayoutColumn> {
        return this.listLayoutColumnsService;
    }

    /**
     * Retourne le contexte utilisé par la source de données de la liste.
     */
    protected override getDatasourceContext(): Context {
        return { property: 'listLayoutId', value: this._context.item.id };
    }

    /**
     * Retourne les infos de tri par défaut de la liste.
     */
    protected override getDefaultSortInfos(): SortInfos {
        return new SortInfos('position', 'asc');
    }

    /**
     * Retourne le droit necessaire pour pouvoir crééer un enregistrement.
     */
    public override getCreationRight(): Rights {
        return Rights.ListLayoutCreate;
    }

    /**
     * Retourne le droit necessaire pour pouvoir mettre à jour un enregistrement.
     */
    protected override getUpdateRight(): Rights {
        return Rights.ListLayoutEdit;
    }

    /**
     * Retourne le droit necessaire pour pouvoir supprimer un enregistrement.
     */
    protected override getDeletionRight(): Rights {
        return Rights.ListLayoutDelete;
    }

    protected override getSortablesItems(item: any): Observable<any[]> {
        return of(
            this.dataSource.datas
                .filter((x) => x.nonEditable === false)
                .map((x) => {
                    return {
                        id: x.attributeId,
                        name: this.gridView.listItems['attributeId'].find((y) => y.value === x.attributeId.toString()).text,
                        order: x.position,
                    };
                })
                .sort((a, b) => {
                    return a.order < b.order ? -1 : 1;
                })
        );
    }
}
